import { useState } from 'react';
import { Link2, Search } from 'lucide-react';
import { Button, Input, Badge, Chip } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useNavigate } from 'react-router-dom';
import { useTicketSearch } from '@/api/tickets';
import { useLinkedTicket } from '@/api/tickets-workflow';
import type { Ticket } from '@/api/tickets';

interface LinkTicketSectionProps {
  ticket: Ticket;
}

/**
 * Displays the ticket's linked ticket_groups (chip list) and provides a
 * search-picker to link or unlink tickets via PUT /tickets/:id/linked.
 *
 * ticket_groups is string[] of ticket IDs. Linking = union; unlinking = remove.
 */
export function LinkTicketSection({ ticket }: LinkTicketSectionProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  const linkedIds: string[] = Array.isArray(ticket.ticket_groups)
    ? ticket.ticket_groups
    : [];

  const searchEnabled = query.trim().length > 0;
  const { data: searchData, isLoading: searching } = useTicketSearch(
    searchEnabled ? query.trim() : '',
  );
  const searchResults = (searchData?.results ?? []).filter(
    (r) => r.ticket_id !== ticket.ticket_id,
  );

  const linkMutation = useLinkedTicket();

  const handleLink = async (targetId: number) => {
    const targetStr = String(targetId);
    if (linkedIds.includes(targetStr)) {
      toast.show({ tone: 'info', title: 'Already linked', description: `#${targetId} is already linked.` });
      return;
    }
    const next = [...linkedIds, targetStr];
    try {
      await linkMutation.mutateAsync({ ticket_id: ticket.ticket_id, ticket_groups: next });
      toast.success(`Linked #${targetId}`);
      setQuery('');
      setShowPicker(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      toast.error('Failed to link ticket', { description: msg });
    }
  };

  const handleUnlink = async (targetStr: string) => {
    const next = linkedIds.filter((id) => id !== targetStr);
    try {
      await linkMutation.mutateAsync({ ticket_id: ticket.ticket_id, ticket_groups: next });
      toast.success(`Unlinked #${targetStr}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      toast.error('Failed to unlink ticket', { description: msg });
    }
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--vn-space-2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <Link2 size={14} style={{ color: 'var(--vn-fg-muted)' }} />
          <span
            style={{
              fontSize: 'var(--vn-font-size-xs)',
              fontWeight: 'var(--vn-font-weight-semibold)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--vn-fg-muted)',
            }}
          >
            Linked Tickets
          </span>
          {linkedIds.length > 0 && (
            <Badge size="sm" tone="neutral">
              {linkedIds.length}
            </Badge>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowPicker((v) => !v)}
        >
          <Link2 size={12} style={{ marginRight: 4 }} />
          {showPicker ? 'Cancel' : 'Link'}
        </Button>
      </div>

      {/* Linked chips */}
      {linkedIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
          {linkedIds.map((id) => (
            <LinkedTicketChip
              key={id}
              ticketIdStr={id}
              onNavigate={() => navigate(`/tickets/${id}`)}
              onRemove={() => handleUnlink(id)}
              removing={linkMutation.isPending}
            />
          ))}
        </div>
      )}

      {linkedIds.length === 0 && !showPicker && (
        <p style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', margin: 0 }}>
          No linked tickets.
        </p>
      )}

      {/* Search picker */}
      {showPicker && (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          <div style={{ position: 'relative' }}>
            <Input
              value={query}
              placeholder="Search by ticket ID or title…"
              startSlot={<Search size={14} />}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {searchEnabled && (
            <div
              style={{
                border: '1px solid var(--vn-border-default)',
                borderRadius: 'var(--vn-radius-md)',
                overflow: 'hidden',
                maxHeight: 240,
                overflowY: 'auto',
              }}
            >
              {searching && (
                <div style={{ padding: 'var(--vn-space-3)', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                  Searching…
                </div>
              )}
              {!searching && searchResults.length === 0 && (
                <div style={{ padding: 'var(--vn-space-3)', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                  No tickets found.
                </div>
              )}
              {searchResults.map((r) => (
                <Button
                  key={r.ticket_id}
                  variant="ghost"
                  onClick={() => handleLink(r.ticket_id)}
                  disabled={linkMutation.isPending || linkedIds.includes(String(r.ticket_id))}
                  style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'flex-start',
                    borderRadius: 0,
                    borderBottom: '1px solid var(--vn-border-subtle)',
                    fontSize: 'var(--vn-font-size-xs)',
                    color: 'var(--vn-fg-default)',
                    gap: 'var(--vn-space-1)',
                  }}
                >
                  <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
                    #{r.ticket_id}
                  </span>
                  <span style={{ color: 'var(--vn-fg-muted)' }}>
                    {r.ticket_type_title}
                  </span>
                  {r.customer_name && (
                    <span style={{ color: 'var(--vn-fg-subtle)' }}>
                      · {r.customer_name}
                    </span>
                  )}
                  {linkedIds.includes(String(r.ticket_id)) && (
                    <span style={{ color: 'var(--vn-success-text)' }}>
                      Already linked
                    </span>
                  )}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Single linked ticket chip — label from ID only, no per-chip query. */
function LinkedTicketChip({
  ticketIdStr,
  onNavigate,
  onRemove,
  removing,
}: {
  ticketIdStr: string;
  onNavigate: () => void;
  onRemove: () => void;
  removing: boolean;
}) {
  const label = `Ticket #${ticketIdStr}`;

  return (
    <Chip
      size="sm"
      tone="neutral"
      disabled={removing}
      onClick={onNavigate}
      onRemove={onRemove}
      removeLabel={`Unlink ${ticketIdStr}`}
      title={label}
    >
      {label}
    </Chip>
  );
}
